import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format, addDays } from "date-fns";
import { Minus, Plus, CheckCircle2, MessageCircle, ShieldCheck, AlertCircle, Users, Clock } from "lucide-react";
import { toast } from "sonner";
import { useSite } from "@/i18n/SiteContext";
import { api, errMsg, euro, waLink } from "@/lib/api";
import { DatePicker, toISODate, LOCALES } from "@/components/site/DatePicker";
import { Label, inputCls } from "@/components/site/QuickRequestForm";
import { tourTitle } from "@/components/site/PackageCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { computeTotal, centsToEuro, isPerGroup, tourPricing, capacity, priceUnitLabel, basePriceEuro, LANG_NAMES } from "@/lib/pricing";

const HARD_MAX = 30;

const tomorrow = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 1);
  return d;
};

/* Works for a single tour (tour prop) or a multi-day package (pkg prop). */
export const BookingWidget = ({ tour, pkg, title }) => {
  const { t, lang, settings } = useSite();
  const item = pkg || tour;
  const isPkg = !!pkg;
  const perGroup = !isPkg && isPerGroup(tour);
  const pricing = isPkg ? null : tourPricing(tour);
  const tourLangs = (!isPkg && tour.languages?.length ? tour.languages : ["it", "en", "fr", "es"]);
  const [date, setDate] = useState();
  const [guests, setGuests] = useState(perGroup ? Math.min(2, pricing.base_participants || 1) : 2);
  const [language, setLanguage] = useState(tourLangs.includes(lang) ? lang : tourLangs[0]);
  const [timeslot, setTimeslot] = useState("");
  const [privacy, setPrivacy] = useState(false);
  const [f, setF] = useState({ name: "", email: "", phone: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [quoteSent, setQuoteSent] = useState(false);
  const [avail, setAvail] = useState(null);

  const cap = isPkg ? item.max_guests || null : capacity(tour);
  const iso = toISODate(date);
  const left = iso && avail?.remaining?.[iso] !== undefined ? avail.remaining[iso] : null;
  const maxSelectable = Math.min(HARD_MAX, cap || HARD_MAX, left !== null ? left : HARD_MAX);
  const tooMany = left !== null && guests > left;
  const price = isPkg ? { total_cents: Math.round(item.price * 100) * guests, quoteRequired: false } : computeTotal(tour, guests);
  const manual = !!avail?.manual_confirmation;

  const loadAvail = useCallback(() => {
    const url = isPkg ? `/packages/${item.slug}/availability` : `/tours/${item.slug}/availability`;
    api.get(url).then((r) => setAvail(r.data)).catch(() => setAvail(null));
  }, [isPkg, item.slug]);

  useEffect(() => { loadAvail(); }, [loadAvail]);

  useEffect(() => {
    if (!iso || !avail) return;
    if ((avail.full || []).includes(iso) || (avail.closed || []).includes(iso)) setDate(undefined);
  }, [avail, iso]);

  const validate = () => {
    if (!date) { toast.error(t("booking.chooseDate")); return false; }
    if (!f.name || !f.email) { toast.error(t("err.required")); return false; }
    if (!privacy) { toast.error(t("booking.acceptPrivacy")); return false; }
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    if (tooMany) return toast.error(t("avail.tooMany", { n: left }));
    if (price.quoteRequired) return requestQuote();
    setLoading(true);
    try {
      const body = { date: iso, guests, ...f, lang, privacy_accepted: true, language: isPkg ? "" : language, timeslot: isPkg ? "" : timeslot,
        ...(isPkg ? { package_slug: item.slug } : { tour_slug: item.slug }) };
      const r = await api.post("/bookings", body);
      setResult(r.data);
      loadAvail();
    } catch (err) {
      toast.error(errMsg(err, t("err.generic")));
      if (err?.response?.status === 409) loadAvail();
    } finally {
      setLoading(false);
    }
  };

  /* Private tours above their base size: the supplement is not published, so we send a quote request instead of a booking. */
  const requestQuote = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      await api.post("/inquiries", {
        kind: "dates", name: f.name, email: f.email, phone: f.phone, lang, privacy_accepted: true,
        date_from: iso, date_to: iso, guests: String(guests), interests: title,
        message: `${t("price.quoteMsg", { tour: title, n: guests, date: iso })}${timeslot ? ` · ${timeslot}` : ""}${f.notes ? `\n${f.notes}` : ""}`,
      });
      setQuoteSent(true);
      toast.success(t("price.quoteSent"));
    } catch (err) {
      toast.error(errMsg(err, t("err.generic")));
    } finally {
      setLoading(false);
    }
  };

  const waText = isPkg ? t("pkg.wa", { pkg: title }) : t("wa.tour", { tour: title });
  const wa = settings.whatsapp ? (
    <a href={waLink(settings.whatsapp, waText)} target="_blank" rel="noopener noreferrer" data-testid="booking-whatsapp-button"
      className="w-full flex items-center justify-center gap-2 px-6 py-3.5 border border-ocean/80 text-ocean text-xs font-medium tracking-[0.18em] uppercase rounded-full hover:bg-ocean hover:text-ivory btn-lift">
      <MessageCircle size={16} /> {t("booking.chat")}
    </a>
  ) : null;

  const locale = LOCALES[lang];

  if (quoteSent) {
    return (
      <div className="bg-white rounded-3xl shadow-soft-xl border border-sand/70 p-8 text-center" data-testid="quote-success">
        <CheckCircle2 size={48} className="mx-auto text-sage-dark" />
        <h3 className="font-serif text-3xl text-ocean mt-5">{t("dates.sentTitle")}</h3>
        <p className="mt-3 text-sm text-ocean/65 leading-relaxed">{t("price.quoteSent")}</p>
        <div className="mt-6">{wa}</div>
      </div>
    );
  }

  if (result) {
    return (
      <div className="bg-white rounded-3xl shadow-soft-xl border border-sand/70 p-8 text-center" data-testid="booking-success">
        <CheckCircle2 size={48} className="mx-auto text-sage-dark" />
        <h3 className="font-serif text-3xl text-ocean mt-5">{t("booking.successTitle")}</h3>
        <p className="mt-3 text-sm text-ocean/65 leading-relaxed">{t("booking.successText", { email: result.email, ref: result.ref })}</p>
        <div className="mt-6 bg-ivory rounded-2xl p-5 text-left text-sm space-y-2">
          <div className="flex justify-between"><span className="text-ocean/55">Ref.</span><span className="font-semibold text-ocean" data-testid="booking-success-ref">{result.ref}</span></div>
          <div className="flex justify-between"><span className="text-ocean/55">{isPkg ? t("pkg.startDate") : t("booking.date")}</span><span className="text-ocean">{result.date}</span></div>
          <div className="flex justify-between"><span className="text-ocean/55">{t("booking.guests")}</span><span className="text-ocean">{result.guests}</span></div>
          {result.language && <div className="flex justify-between"><span className="text-ocean/55">{t("booking.language")}</span><span className="text-ocean">{LANG_NAMES[result.language] || result.language}</span></div>}
          {result.timeslot && <div className="flex justify-between"><span className="text-ocean/55">{t("booking.time")}</span><span className="text-ocean">{result.timeslot}</span></div>}
          <div className="flex justify-between"><span className="text-ocean/55">{t("booking.total")}</span><span className="font-serif text-lg text-ocean">{result.total_cents !== undefined ? centsToEuro(result.total_cents) : euro(result.total)}</span></div>
        </div>
        <button onClick={() => { setResult(null); setDate(undefined); setPrivacy(false); }} className="mt-6 text-sm font-medium text-sea hover:text-ocean underline underline-offset-4" data-testid="booking-another">
          {t("booking.another")}
        </button>
        <div className="mt-6">{wa}</div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white rounded-3xl shadow-soft-xl border border-sand/70 overflow-hidden" data-testid="booking-widget">
      <div className="px-6 md:px-8 pt-7 pb-6 border-b border-sand/70 flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-medium tracking-super-wide uppercase text-sea">{isPkg ? t("pkg.book") : t("booking.title")}</p>
          <p className="text-sm text-ocean/60 mt-2 leading-relaxed">{isPkg ? t("pkg.consecutive") : t("booking.sub")}</p>
        </div>
        <div className="text-right shrink-0" data-testid="booking-price">
          {isPkg && <p className="text-[11px] text-ocean/45 line-through">{euro(item.full_price)}</p>}
          {!isPkg && !perGroup && <p className="text-[11px] tracking-[0.18em] uppercase text-ocean/50">{t("card.from")}</p>}
          <p className="font-serif text-3xl text-ocean leading-none mt-1">{isPkg ? euro(item.price) : basePriceEuro(tour)}</p>
          <p className="text-[11px] text-ocean/50 mt-1 max-w-[140px] leading-snug" data-testid="booking-price-unit">{isPkg ? t("detail.pp") : priceUnitLabel(tour, t)}</p>
        </div>
      </div>
      <div className="p-6 md:p-8">
        <div className="space-y-5">
          <div>
            <Label>{isPkg ? t("pkg.startDate") : t("booking.date")} *</Label>
            <DatePicker value={date} onChange={setDate} placeholder={t("booking.pickDate")} testId="booking-date-button" minDate={tomorrow()} availability={avail} showLegend={!manual} />
            {left !== null && (
              <p className={`mt-2 flex items-center gap-2 text-xs ${tooMany ? "text-[#9A3B2E]" : "text-sea"}`} data-testid="booking-seats-left">
                <AlertCircle size={14} /> {tooMany ? t("avail.tooMany", { n: left }) : t("avail.left", { n: left })}
              </p>
            )}
            {isPkg && date && (
              <ol className="mt-3 space-y-1.5 bg-ivory rounded-xl p-4" data-testid="booking-package-schedule">
                {item.tours.map((x, i) => (
                  <li key={x.slug} className="flex gap-3 text-xs text-ocean/75">
                    <span className="font-semibold text-ocean w-24 shrink-0">{format(addDays(date, i), "EEE d MMM", { locale })}</span>
                    <span className="truncate">{tourTitle(x, lang)}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>

          {!isPkg && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>{t("booking.language")}</Label>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger className="h-12 rounded-xl border-sand bg-white text-ocean" data-testid="booking-language-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {tourLangs.map((l) => <SelectItem key={l} value={l} data-testid={`booking-language-${l}`}>{LANG_NAMES[l] || l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("booking.time")}</Label>
                {tour.timeslots?.length ? (
                  <Select value={timeslot} onValueChange={setTimeslot}>
                    <SelectTrigger className="h-12 rounded-xl border-sand bg-white text-ocean" data-testid="booking-timeslot-select"><SelectValue placeholder={t("booking.timeAny")} /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {tour.timeslots.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="relative">
                    <Clock size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-ocean/40" />
                    <input className={`${inputCls} pl-10`} placeholder={t("booking.timePh")} value={timeslot} maxLength={40} onChange={(e) => setTimeslot(e.target.value)} data-testid="booking-timeslot-input" />
                  </div>
                )}
              </div>
            </div>
          )}

          <div>
            <Label>{t("booking.guests")} *</Label>
            <div className="flex items-center justify-between h-12 px-2 bg-white border border-sand rounded-xl">
              <button type="button" aria-label={t("booking.remove")} data-testid="booking-guests-minus" onClick={() => setGuests((g) => Math.max(1, g - 1))} disabled={guests <= 1}
                className="w-11 h-11 rounded-lg flex items-center justify-center text-ocean hover:bg-ivory disabled:opacity-30"><Minus size={16} /></button>
              <span className="font-medium text-ocean" data-testid="booking-guests-value">{guests}</span>
              <button type="button" aria-label={t("booking.add")} data-testid="booking-guests-plus" onClick={() => setGuests((g) => Math.min(maxSelectable, g + 1))} disabled={guests >= maxSelectable}
                className="w-11 h-11 rounded-lg flex items-center justify-center text-ocean hover:bg-ivory disabled:opacity-30"><Plus size={16} /></button>
            </div>
            {perGroup && !price.quoteRequired && (
              <p className="mt-2 flex items-center gap-2 text-xs text-ocean/60" data-testid="booking-group-hint"><Users size={14} className="text-sage-dark" />{t("price.privateFor", { n: pricing.base_participants })}</p>
            )}
          </div>
          <div>
            <Label>{t("booking.name")} *</Label>
            <input required className={inputCls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="booking-name-input" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>{t("booking.email")} *</Label>
              <input required type="email" className={inputCls} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="booking-email-input" />
            </div>
            <div>
              <Label>{t("booking.phone")}</Label>
              <input className={inputCls} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="booking-phone-input" />
            </div>
          </div>
          <div>
            <Label>{t("booking.notes")}</Label>
            <textarea rows={2} className={`${inputCls} h-auto py-3 resize-none`} placeholder={t("booking.notesPh")} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="booking-notes-input" />
          </div>
          <label className="flex items-start gap-3 text-xs text-ocean/70 leading-relaxed cursor-pointer" data-testid="booking-privacy-label">
            <Checkbox checked={privacy} onCheckedChange={(v) => setPrivacy(!!v)} className="mt-0.5" data-testid="booking-privacy-checkbox" />
            <span>
              {t("booking.privacy")} *{" "}
              <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="underline underline-offset-4 text-sea hover:text-ocean" data-testid="booking-privacy-link">{t("legal.privacy")}</Link>
            </span>
          </label>
        </div>

        {price.quoteRequired ? (
          <div className="mt-6 bg-sand-brand/30 border border-sand rounded-2xl px-5 py-4" data-testid="booking-quote-panel">
            <p className="text-xs font-medium tracking-[0.18em] uppercase text-ocean">{t("price.quoteTitle", { n: pricing.base_participants })}</p>
            <p className="text-sm text-ocean/70 mt-2 leading-relaxed">{t("price.quoteText", { n: pricing.base_participants })}</p>
          </div>
        ) : (
          <div className="mt-6 flex items-center justify-between gap-4 bg-ivory rounded-2xl px-5 py-4 border border-sand/60">
            <div>
              <p className="text-xs font-medium tracking-[0.18em] uppercase text-ocean">{perGroup ? t("booking.groupPrice") : t("booking.total")}</p>
              <p className="text-xs text-ocean/65 mt-1">
                {perGroup ? t("price.privateFor", { n: pricing.base_participants }) : t("price.perPersonTotal", { price: isPkg ? euro(item.price) : basePriceEuro(tour), n: guests })}
              </p>
            </div>
            <p className="font-serif text-3xl text-ocean" data-testid="booking-total">{centsToEuro(price.total_cents)}</p>
          </div>
        )}

        <button type="submit" disabled={loading || tooMany} data-testid={price.quoteRequired ? "booking-quote-button" : "booking-submit-button"}
          className="mt-6 w-full px-6 py-4 bg-ocean text-ivory text-xs font-medium tracking-[0.2em] uppercase rounded-full hover:bg-ocean-700 disabled:opacity-60 shadow-soft btn-lift">
          {loading ? t("booking.submitting") : price.quoteRequired ? t("price.quoteBtn") : t("booking.submit")}
        </button>
        <p className="mt-4 flex gap-2 text-xs text-ocean/70 leading-relaxed"><ShieldCheck size={15} className="text-sage-dark shrink-0 mt-0.5" />{t("booking.noPayment")}</p>
        {manual && <p className="mt-2 flex gap-2 text-xs text-ocean/60 leading-relaxed" data-testid="booking-manual-note"><AlertCircle size={15} className="text-sea shrink-0 mt-0.5" />{t("booking.manualNote")}</p>}
        <div className="mt-5">{wa}</div>
      </div>
    </form>
  );
};
